// src/services/storage/indexedDB.ts

/**
 * IndexedDBサービス - localStorageの代替として使用
 *
 * iOS WKWebView（Flutter内webview）では次の2系統で IndexedDB が一過性に固まる/失敗する：
 *  ① ネイティブカメラ表示中のサスペンドで open()/transaction が無応答化（/camera 集中）
 *  ② ページ読込時、並列APIが各自 open() して同一DBへ同時オープンし onerror（/menu 集中）
 * トークン読取がこれで失敗すると API が全滅するため、本モジュールは以下で堅牢化する：
 *  - 接続をシングルトン化（1本を使い回し、都度 close しない）→ ② の同時オープン競合を解消
 *  - メモリ + localStorage のミラーを持ち、IndexedDB が使えないときの読取フォールバックにする → ①
 *    ただし localStorage ミラーは約5MB枠を溢れさせないよう、認証・セッション系の小さいキー
 *    （LS_MIRROR_KEYS）に限定する。大物（base64画像等）はメモリキャッシュのみでフォールバックする。
 *  - 接続失効時は再オープンして1回だけ再試行（visibilitychange 復帰でも接続を作り直す）
 */

// データベース設定
const DB_NAME = 'acsDb';
const DB_VERSION = 2; // バージョンを上げる
const STORE_NAME = 'keyValueStore';
const OPEN_TIMEOUT_MS = 3000;

// ---------------------------------------------------------------------------
// フォールバック層（メモリ + localStorage ミラー）
// ---------------------------------------------------------------------------
// IndexedDB を正とするが、一過性に読めないときのために同じ値をここへも書いておく。
const memCache = new Map<string, string>();

// localStorage ミラー対象キーのホワイトリスト。
// ミラーの目的は「IndexedDB が一過性に固まっても認証を継続し API を全滅させない」こと。
// よって復旧に必須で"小さい"認証・セッション系だけを対象にする。
// scanResult / backSideImage / editResultBackup のような base64 画像を含む大物は、
// 約5MB の localStorage 枠を溢れさせ QuotaExceeded でミラー機構ごと壊すため対象外にする。
// （大物の一過性フォールバックは下のメモリキャッシュ memCache が担当する）
const LS_MIRROR_KEYS = new Set<string>([
  'token',
  'refreshToken',
  'tokenTimestamp',
  'webTokenRefreshedAt',
  'user',
  'companyId',
]);
const shouldMirror = (key: string): boolean => LS_MIRROR_KEYS.has(key);

const lsGet = (key: string): string | null => {
  // 対象外キーは（もう LS に書いていないので）参照しない
  if (!shouldMirror(key)) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const lsSet = (key: string, value: string): void => {
  try {
    if (shouldMirror(key)) {
      localStorage.setItem(key, value);
    } else {
      // 対象外キーが旧ロジックで LS に残っていれば掃除する（quota 圧迫の解消）
      localStorage.removeItem(key);
    }
  } catch {
    // プライベートモード等で失敗しても致命ではない
  }
};
const lsRemove = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // noop
  }
};

// ---------------------------------------------------------------------------
// シングルトン接続
// ---------------------------------------------------------------------------
// 進行中 or 確立済みの接続。失敗時は null に戻して次回再オープンする。
let dbPromise: Promise<IDBDatabase> | null = null;

const invalidateDb = (): void => {
  dbPromise = null;
};

/**
 * 実際に indexedDB.open を行う（タイムアウト付き）。
 */
const rawOpen = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn();
    };

    // open が onsuccess/onerror/onblocked のいずれも発火せず固まると、
    // getItem→restoreSession が永久に解決せず「読み込み中」のまま固まる。
    // これを防ぐため必ずタイムアウトで決着させる。
    const timer = setTimeout(() => {
      settle(() => reject(new Error('IndexedDB open timeout')));
    }, OPEN_TIMEOUT_MS);

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      settle(() => reject(e instanceof Error ? e : new Error(String(e))));
      return;
    }

    request.onerror = () => {
      settle(() => reject(new Error('IndexedDBを開くのに失敗しました')));
    };

    // 他の接続が開いていてブロックされた場合も固まらせない
    request.onblocked = () => {
      settle(() => reject(new Error('IndexedDB open blocked')));
    };

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // 接続が失われた/バージョン変更されたらキャッシュを無効化して次回再オープンさせる
      db.onclose = () => {
        invalidateDb();
      };
      db.onversionchange = () => {
        try {
          db.close();
        } catch {
          // noop
        }
        invalidateDb();
      };
      settle(() => resolve(db));
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // keyValueStore が無ければ作成（既存データは消さない）
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
};

/**
 * シングルトン接続を取得（無ければ開く）。開けなければキャッシュせず throw。
 */
const openDB = (): Promise<IDBDatabase> => {
  if (dbPromise) return dbPromise;
  const p = rawOpen().catch((e) => {
    // 失敗した接続はキャッシュしない（次回また試せるように）
    if (dbPromise === p) dbPromise = null;
    throw e;
  });
  dbPromise = p;
  return p;
};

// 画面がバックグラウンドから復帰した直後は接続が死んでいることがあるため、
// 次の操作で作り直せるようキャッシュを捨てる（遅延再オープン）。
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      invalidateDb();
    }
  });
}

/**
 * ストアに対する1リクエストを実行する。
 * 接続が死んでいた場合は1回だけ再オープンして再試行する。
 * put/delete/get はいずれも冪等なので再試行は安全。
 */
const runRequest = async <T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> => {
  const runOn = (db: IDBDatabase): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      let tx: IDBTransaction;
      try {
        tx = db.transaction([STORE_NAME], mode);
      } catch (e) {
        // 接続が閉じている（InvalidStateError 等）→ 上位で再試行
        reject(e instanceof Error ? e : new Error(String(e)));
        return;
      }
      const store = tx.objectStore(STORE_NAME);
      let request: IDBRequest<T>;
      try {
        request = op(store);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
        return;
      }
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB request error'));
      tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    });

  // 1回目：接続確立が失敗（timeout/blocked/onerror）ならそのまま throw（再オープンしない）
  const db = await openDB();
  try {
    return await runOn(db);
  } catch (e) {
    // 生きているはずの接続でトランザクションが失敗 → 接続失効の可能性。
    // 一度だけ再オープンして再試行する。
    invalidateDb();
    let db2: IDBDatabase;
    try {
      db2 = await openDB();
    } catch {
      throw e; // 再オープンも不可なら元エラーを投げる
    }
    return await runOn(db2);
  }
};

/**
 * データを保存
 * @param key キー
 * @param value 値
 * @returns Promise<void>
 */
export const setItem = async (key: string, value: string): Promise<void> => {
  // まずミラー（メモリ + localStorage）へ即時保存。IndexedDB が詰まっても値は保持される。
  memCache.set(key, value);
  lsSet(key, value);

  try {
    await runRequest('readwrite', (store) => store.put(value, key));
  } catch (error) {
    // IndexedDB への保存が失敗してもミラーには保存済みなので致命ではない
    console.warn(`IndexedDB setItem fallback (${key}):`, error);
  }
};

/**
 * データを取得
 * @param key キー
 * @returns Promise<string | null>
 */
export const getItem = async (key: string): Promise<string | null> => {
  try {
    const result = (await runRequest<string | undefined>(
      'readonly',
      (store) => store.get(key),
    )) ?? null;

    // 取得できた値でミラーを同期
    if (result === null) {
      memCache.delete(key);
      lsRemove(key);
    } else {
      memCache.set(key, result);
      lsSet(key, result);
    }
    return result;
  } catch (error) {
    // IndexedDB が一過性に使えない（カメラ中サスペンド/並列open競合等）
    // → メモリ → localStorage の順でフォールバックする
    console.warn(`IndexedDB getItem fallback (${key}):`, error);
    if (memCache.has(key)) {
      return memCache.get(key) ?? null;
    }
    return lsGet(key);
  }
};

/**
 * データを削除
 * @param key キー
 * @returns Promise<void>
 */
export const removeItem = async (key: string): Promise<void> => {
  // ミラーからも削除（ログアウト等で確実に消す）
  memCache.delete(key);
  lsRemove(key);

  try {
    await runRequest('readwrite', (store) => store.delete(key));
  } catch (error) {
    console.warn(`IndexedDB removeItem fallback (${key}):`, error);
  }
};

// ---------------------------------------------------------------------------
// 一回限りのマイグレーション
// ---------------------------------------------------------------------------
// 旧バージョンは顧客ごとに customer_rank_<顧客ID> というキーを書き込んでいたが、
// 現在はランク色を rank_colors（ランク名→色の全体マップ）に一本化しており、
// これらのキーは読み書きしていない。過去にブラウザへ溜まった孤児キーは放置すると
// 顧客数ぶん残り続けるため、起動時に一度だけまとめて削除する。
const LEGACY_CUSTOMER_RANK_PREFIX = 'customer_rank_';
const MIGRATION_FLAG_KEY = '__migrated_legacy_customer_rank__';

/**
 * 旧 customer_rank_<顧客ID> キーを一括削除する（冪等・失敗しても無害）。
 *
 * ポイント: どんなキーが存在するかは getItem 単体では分からないため、
 * ストア内の全キーを列挙する IDBObjectStore.getAllKeys() を使い、
 * プレフィックス customer_rank_ に一致するものだけを削除する。
 * （旧コードにあった getAllItems は用途過剰だったため削除済み。ここでは
 *  「全キー列挙 → プレフィックス絞り込み → 削除」に必要な getAllKeys だけを使う）
 */
export const purgeLegacyCustomerRankKeys = async (): Promise<void> => {
  try {
    // 既に実行済みなら何もしない（完了フラグは IndexedDB に保持）
    if ((await getItem(MIGRATION_FLAG_KEY)) === 'done') return;

    const db = await openDB();

    // ストア内の全キーを取得（getAllKeys）
    const allKeys: IDBValidKey[] = await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_NAME], 'readonly');
      const request = tx.objectStore(STORE_NAME).getAllKeys();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('getAllKeys に失敗しました'));
    });

    // customer_rank_ で始まる孤児キーだけに絞り込む
    const legacyKeys = allKeys.filter(
      (k): k is string => typeof k === 'string' && k.startsWith(LEGACY_CUSTOMER_RANK_PREFIX),
    );

    // IndexedDB・メモリ・localStorage の3層すべてから削除する
    for (const key of legacyKeys) {
      await removeItem(key);
    }

    // 超旧版（localStorage 直書き時代）の名残が LS 側にだけ残っている場合も掃除する。
    // getAllKeys は IndexedDB のキーしか列挙しないため、LS は別途走査する必要がある。
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LEGACY_CUSTOMER_RANK_PREFIX)) {
          localStorage.removeItem(k);
        }
      }
    } catch {
      // プライベートモード等は無視
    }

    // 再実行を避けるため完了フラグを立てる
    await setItem(MIGRATION_FLAG_KEY, 'done');

    if (legacyKeys.length > 0) {
      console.info(`[migration] 旧 customer_rank_* を ${legacyKeys.length} 件削除しました`);
    }
  } catch (error) {
    // 失敗しても致命ではない（フラグが立たないので次回起動で再試行される）
    console.warn('[migration] customer_rank_* の掃除に失敗しました:', error);
  }
};

// 起動時に一度だけ実行する（fire-and-forget。結果を待つ必要はない）
if (typeof window !== 'undefined') {
  purgeLegacyCustomerRankKeys().catch(() => {});
}

/**
 * 現在の詳細表示顧客IDの保存キー
 */
export const CURRENT_DETAIL_CUSTOMER_ID_KEY = 'current_detail_customer_id';

/**
 * IndexedDBのストレージラッパー（localStorageと互換性のあるインターフェース）
 */
export const indexedDBStorage = {
  getItem,
  setItem,
  removeItem,
};
